// @ts-strict-ignore
import { useSelector } from 'react-redux';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import {
  selectActiveNodeEditorTab,
  selectDiffedActions,
  selectFocusedActionForDiff,
  selectInitialOutcomeActions,
  selectInitialWorkflowData,
  selectOutcomeActions,
  selectPublicVersion,
  selectWorkflowData,
} from 'flow/store/redux/selectors';
import { getActionPrecedenceIndex, sortActionsByPrecedenceIndex } from 'flow/service/actions';
import { useInitialWorkflowStore } from './useInitialWorkflowStore';
import {
  StyledDiffViewContainer,
  StyledDiffViewPane,
  StyledBadgeContainer,
  StyledDiffViewGrid,
  StyledDiffViewColumn,
} from './styles';
import { Badge } from '@usealloy/component-library';
import { StyledCodeStyleText } from 'flow/editor/styles';
import type { Pane } from './DiffContext';
import DiffContextProvider, { panes } from './DiffContext';
import { Operation } from 'flow/service/changelog/types';
import type { Action } from 'flow/store/engine/types';
import { DiffCard } from './DiffCard';

type DiffViewSplitProps = {
  children: ReactNode;
  action?: Action;
  showBadges?: boolean;
};

export { getDiffStatusById } from './DiffContext';

/**
 * Renders `children` twice, side by side: "Existing" against a store/form scoped to the
 * initial snapshot, and "Modified" against the ambient store/form. Each pane is wrapped in
 * its own DiffContextProvider so children can diff against each other.
 */
export function DiffViewSplit({ children, action, showBadges = true }: DiffViewSplitProps) {
  const initialWorkflowData = useSelector(selectInitialWorkflowData);
  const currentWorkflowData = useSelector(selectWorkflowData);
  const activeNodeEditorTab = useSelector(selectActiveNodeEditorTab);
  const selectedFocusedAction = useSelector(selectFocusedActionForDiff);
  const diffedActions = useSelector(selectDiffedActions);
  const initialStore = useInitialWorkflowStore();
  const workflowVersion = useSelector(selectPublicVersion);


  const focusedPublicId = action?.publicId ?? selectedFocusedAction?.publicId;
  const initialFocusedAction = initialWorkflowData?.actions?.[focusedPublicId];
  const currentFocusedAction = action ?? currentWorkflowData?.actions?.[focusedPublicId];

  // Field-level highlighting only makes sense for a MODIFIED action
  const focusedActionDiffStatus = diffedActions[focusedPublicId];
  const highlightFields = focusedActionDiffStatus === Operation.Modify;

  function renderCorrectBadge(pane: Pane, actionDiffStatus: Operation) {
    if (pane === panes.left) {
      if (actionDiffStatus === Operation.Modify) return <Badge status="neutral">Existing</Badge>;
      if (actionDiffStatus === Operation.Remove) return <Badge status="danger">Removed</Badge>;
      if (actionDiffStatus === Operation.Add) {
        return (
          <StyledCodeStyleText>This node was added in v{workflowVersion + 1}.</StyledCodeStyleText>
        );
      }
      return '';
    }

    if (actionDiffStatus === Operation.Modify) return <Badge status="attention">Modified</Badge>;
    if (actionDiffStatus === Operation.Add) return <Badge status="success">Added</Badge>;
    if (actionDiffStatus === Operation.Remove) {
      return (
        <StyledCodeStyleText>This node was removed in v{workflowVersion + 1}.</StyledCodeStyleText>
      );
    }
    return '';
  }

  return (
    <StyledDiffViewContainer>
      <StyledDiffViewPane>
        <DiffContextProvider
          highlightFields={highlightFields}
          pane={panes.left}
        >
          {showBadges && (
          <StyledBadgeContainer>
            {renderCorrectBadge(panes.left, focusedActionDiffStatus)}
          </StyledBadgeContainer>
          )}
          <DiffCard
            action={initialFocusedAction}
            pane={panes.left}
            store={initialStore}
            formTab={activeNodeEditorTab}
            child={children}
          />
        </DiffContextProvider>
      </StyledDiffViewPane>
      <StyledDiffViewPane>
        <DiffContextProvider
          highlightFields={highlightFields}
          pane={panes.right}
        >
          {showBadges && (
            <StyledBadgeContainer>
              {renderCorrectBadge(panes.right, focusedActionDiffStatus)}
            </StyledBadgeContainer>
          )}
          <DiffCard
            action={currentFocusedAction}
            pane={panes.right}
            formTab={activeNodeEditorTab}
            child={children}
          />
        </DiffContextProvider>
      </StyledDiffViewPane>
    </StyledDiffViewContainer>
  );
}

type DiffViewActionsSplitProps = {
  children: (action: Action) => ReactNode;
  showBadges?: boolean;
};

function sortOutcomesForDiffColumn(outcomeActions: Action[]): Action[] {
  const hasPrecedence = (action: Action) => (
    typeof getActionPrecedenceIndex(action) === 'number'
  );

  return [
    ...sortActionsByPrecedenceIndex(outcomeActions.filter(hasPrecedence), 'DESC'),
    ...outcomeActions.filter(action => !hasPrecedence(action)),
  ];
}

/**
 * Diffs every outcome action side by side: one
 * badge row per pane, with one column per snapshot
 */
export function DiffViewOutcomeActionsSplit({
  children,
  showBadges = true,
}: DiffViewActionsSplitProps) {
  const initialOutcomeActions = useSelector(selectInitialOutcomeActions);
  const currentOutcomeActions = useSelector(selectOutcomeActions);
  const initialStore = useInitialWorkflowStore();

  const initialColumn = useMemo(
    () => sortOutcomesForDiffColumn(initialOutcomeActions),
    [initialOutcomeActions],
  );
  const currentColumn = useMemo(
    () => sortOutcomesForDiffColumn(currentOutcomeActions),
    [currentOutcomeActions],
  );

  return (
    <StyledDiffViewGrid>
      <StyledDiffViewColumn pane={panes.left}>
        {showBadges && (
          <StyledBadgeContainer>
            <Badge status="neutral">Existing</Badge>
          </StyledBadgeContainer>
        )}
        <DiffContextProvider pane={panes.left}>
          {initialColumn.map(action => (
            <DiffCard
              key={action.publicId}
              action={action}
              child={children(action)}
              pane={panes.left}
              store={initialStore}
            />
          ))}
        </DiffContextProvider>
      </StyledDiffViewColumn>
      <StyledDiffViewColumn pane={panes.right}>
        {showBadges && (
          <StyledBadgeContainer>
            <Badge status="attention">Modified</Badge>
          </StyledBadgeContainer>
        )}
        <DiffContextProvider pane={panes.right}>
          {currentColumn.map(action => (
            <DiffCard
              key={action.publicId}
              action={action}
              child={children(action)}
              pane={panes.right}
            />
          ))}
        </DiffContextProvider>
      </StyledDiffViewColumn>
    </StyledDiffViewGrid>
  );
}
